import { useCallback, useEffect, useMemo } from 'react';

import { useFormik } from 'formik';

import { useGetUser } from 'hooks/api/user/useGetUser';
import useFormikErrors from 'hooks/formik/useFormikErrors';
import type { ProjectNames, RoleNames } from 'hooks/permission/types';

import { logger } from 'utils/logger';
import { ERROR_REASONS } from 'utils/logger/transports/constants';

import type { PermissionsFormikValuesType } from '../types';
import { getProjectByRole } from '../utils';
import { useAppContext } from 'store/AppProvider';

interface UseEnhancedModalProps {
    selectedUserId?: string;
    onClose: () => void;
    mutate: (values: PermissionsFormikValuesType) => void;
    deleteAllRightsHandler?: () => void;
}

const DEFAULT_PERMISSION = { role: '' as RoleNames, projects: [] };

const useEnhancedModal = ({ selectedUserId, onClose, deleteAllRightsHandler, mutate }: UseEnhancedModalProps) => {
    const { appState } = useAppContext();
    const { showErrorMessages } = useFormikErrors();
    const projectList = appState.generalData.projectsList;

    const {
        data: selectedUser,
        error,
        isPending: isLoading,
    } = useGetUser({
        id: selectedUserId,
    });

    useEffect(() => {
        if (error) {
            showErrorMessages({
                errors: error.data,
                showGlobalMessage: true,
                customMessage: 'Error fetching user',
            });
            logger.error(Error('Error fetching user:'), {
                cause: error,
                tags: {
                    reason: ERROR_REASONS.getUser,
                    errorCode: error.status,
                },
            });
        }
    }, [error]);

    const currentRoles = useMemo(() => [...new Set(Object.values(selectedUser?.groups ?? {}))] as RoleNames[], [selectedUser?.groups]);

    const formik = useFormik<PermissionsFormikValuesType>({
        initialValues: {
            email: '',
            permission: [{ ...DEFAULT_PERMISSION }],
        },
        onSubmit: (values) => {
            if (values.permission.length) {
                mutate(values);
            } else {
                deleteAllRightsHandler?.();
            }
            onClose();
        },
    });

    useEffect(() => {
        if (selectedUser) {
            void formik.setFieldValue('email', selectedUser.email);

            currentRoles.forEach((role, index) => {
                void formik.setFieldValue(`permission[${index}]`, {
                    role,
                    projects: getProjectByRole(selectedUser.groups as unknown as Record<RoleNames, ProjectNames>, role),
                });
            });
        }

        return () => {
            formik.resetForm();
        };
    }, [selectedUser, currentRoles]);

    const getFilteredProjectList = useMemo(() => {
        const assignedProjects = formik.values.permission.flatMap((item) => item.projects);
        return projectList.filter((project) => !assignedProjects.includes(project.system_name));
    }, [formik.values.permission, projectList]);

    const handleDeletePermission = async (item: { role?: string; projects?: string[] }) => {
        await formik.setFieldValue(
            'permission',
            formik.values.permission.filter((permission) => permission !== item)
        );
    };

    const handleAddPermission = useCallback(() => {
        void formik.setFieldValue('permission', [...formik.values.permission, { ...DEFAULT_PERMISSION }]);
    }, [formik]);

    const handleDeleteProject = useCallback(
        ({ index, value }: { index: number; value: string }) => {
            void formik.setFieldValue(
                `permission[${index}].projects`,
                formik.values.permission[index]?.projects?.filter((item) => item !== value)
            );
        },
        [formik]
    );

    return {
        formik,
        getFilteredProjectList,
        isLoading,
        handleDeleteProject,
        handleDeletePermission,
        handleAddPermission,
        closeModal: onClose,
    };
};

export { useEnhancedModal };
